import React from 'react';
import { IAutocompleteStreet, DealType, CompletionType } from 'utils/entities';
import { Route } from 'config/routes';
import { useLocale } from 'locale';
import { WithContentSlotTopPadding, WithContentSlotBottomPadding } from 'ds/components/Layout';
import { InsightsSection } from 'ds/components/Insights';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { Section as SectionEnum } from 'components/navigation/subheader/SectionsNavigation';
import { noop } from 'lodash';
import Footer from 'components/footer';
import { SubHeader, Header, StreetCarouselWrapper, InsightsWrapper, PricesWrapper, ChartWrapper, BlogSectionBg } from './styled';
import Section from 'components/unit-page/mobile-map-widget/Section';
import StreetNearbyCarousel from './listings';
import { CenteredLink } from 'ds/components/carousels/carousel/styled';
import { Button } from 'ds/components/button';
import { Seller } from 'components/filters/types';
import DetailHeader from 'components/details-section';
import PriceChart from 'components/widget/trends';
import { PricesTableTitle } from 'components/widget/prices/prices-widget/styled';
import Listings from 'components/widget/prices/section/listings';
import { PricesBorder } from 'components/widget/prices/section/styled';
import ProjectAdBanner from 'components/ads/ProjectAdBanner';
import ProjectAdBannerSource from 'components/ads/ProjectAdBannerSource';
import { AdGetter } from 'components/ads/types';
import BlogSection from 'components/blog-carousel/street-page';
import { Article } from 'store/sagas/apiService/types';


interface StreetPageProps {
  resolvedAddress: IAutocompleteStreet;
  totalPoi: number;
  cityDocId: string;
  dealType: DealType;
  articles: Article[];
}

export const StreetPage: React.FC<StreetPageProps> = ({
  resolvedAddress,
  cityDocId,
  totalPoi,
  dealType,
  articles,
}) => {
  const { t } = useLocale();

  if (!resolvedAddress) return null;
  const { location } = resolvedAddress;

  return (
    <>
      <ScrollSpyProvider onChange={noop}>
        <WithContentSlotBottomPadding>
          <ScrollSpyTarget targetId={SectionEnum.OVERVIEW}>
            <Header>
              <SubHeader>
                <DetailHeader
                  type={CompletionType.Street}
                  zoneName={resolvedAddress.street}
                  city={resolvedAddress.city}
                  location={resolvedAddress.location}
                />
              </SubHeader>
            </Header>
          </ScrollSpyTarget>
          <InsightsWrapper>
            <InsightsSection>
              {location ? (
                <PricesWrapper data-auto="sales-block">
                  <PricesBorder>
                    <PricesTableTitle>{t('widget.prices.tableHeader')}</PricesTableTitle>
                    <Listings />
                  </PricesBorder>
                  <ChartWrapper>
                    <ScrollSpyTarget targetId={SectionEnum.TRENDS}>
                      <PriceChart />
                    </ScrollSpyTarget>
                  </ChartWrapper>
                </PricesWrapper>
              ) : null }
            </InsightsSection>
          </InsightsWrapper>
          {articles && articles.length ? (
            <BlogSectionBg>
              <BlogSection />
            </BlogSectionBg>
          ) : null}
          <ProjectAdBannerSource>
            {(getBanner: AdGetter) => <ProjectAdBanner fullWidth ad={getBanner()} />}
          </ProjectAdBannerSource>
          <WithContentSlotTopPadding>
            <StreetCarouselWrapper data-auto="street-listings-for-sale">
              <Section heading={t('streetPage.sale.label', { street: resolvedAddress.street, count: totalPoi })}>
                <StreetNearbyCarousel />
                {cityDocId && dealType ? (
                  <CenteredLink routeName={Route.Search} routeParams={{ term: [ cityDocId ], dealType, filters: { seller: [ Seller.Developer ] } }}>
                    <Button data-auto="new-projects-button">
                      {t('localPage.adCarousel.link')}
                    </Button>
                  </CenteredLink>
                ) : null}
              </Section>
            </StreetCarouselWrapper>
          </WithContentSlotTopPadding>
        </WithContentSlotBottomPadding>
        <ScrollSpyTarget targetId={SectionEnum.FOOTER}>
          <Footer />
        </ScrollSpyTarget>
      </ScrollSpyProvider>
    </>
  );
};
